import { Button, Table, addToast, showDialogConfirm } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import Loading from '../../../../../components/info/Loading';
import SectionTitle from '../../../../../components/text/SectionTitle';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';

import { FinancialBlockOverview, deleteFinancialBlockConfig, getFinancialBlock } from './api';
import { ConfigModal } from './components/ConfigModal';
import { ProcessesModal } from './components/ProcessesModal';
import { useConfigColumns } from './hooks/useConfigColumns';
import { useProcessColumns } from './hooks/useProcessColumns';
import { getRuleSentence } from './ruleSentence';

const dictPrefix = 'financialPortal.settings.financialBlock';

export default function FinancialBlockSection() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<FinancialBlockOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isProcessesModalOpen, setIsProcessesModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getFinancialBlock();
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.loadError`),
        });
      } else {
        setOverview(data);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // Refresca sin volver a poner `isLoading`: un refresco parcial no debe
  // hacer parpadear la sección entera.
  const handleSaved = useCallback(() => {
    setIsConfigModalOpen(false);
    setIsProcessesModalOpen(false);
    setRefreshToken((prev) => prev + 1);
  }, []);

  const handleEdit = useCallback(() => {
    setIsConfigModalOpen(true);
  }, []);

  const handleDelete = useCallback(() => {
    showDialogConfirm({
      icon: { name: 'warning', color: 'warning' },
      title: t(`${dictPrefix}.confirmDelete.title`),
      subtitle: t(`${dictPrefix}.confirmDelete.subtitle`),
      btnConfirm: {
        text: t(`${dictPrefix}.confirmDelete.confirm`),
        onConfirm: async () => {
          const { error } = await deleteFinancialBlockConfig();
          if (error) {
            addToast({
              icon: 'error',
              color: 'danger',
              text: t(`${dictPrefix}.deleteError`),
            });
            return;
          }
          addToast({
            icon: 'success',
            color: 'success',
            text: t(`${dictPrefix}.deleteSuccess`),
          });
          setRefreshToken((prev) => prev + 1);
        },
      },
      btnClose: { text: t(`${dictPrefix}.confirmDelete.cancel`) },
    });
  }, [t]);

  const configColumns = useConfigColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
  });
  const processColumns = useProcessColumns();

  if (isLoading) {
    return <Loading insideCard />;
  }
  if (!overview) {
    return null;
  }

  const affectedProcesses = overview.processes.filter((p) => p.affected);

  return (
    <>
      <Row className="mt-5">
        <Col xs={12} lg={6}>
          <SectionTitle text={t(`${dictPrefix}.configTitle`)} />
          <p>{t(`${dictPrefix}.configSubtitle`)}</p>
          <Table
            columns={configColumns}
            data={overview.config ? [overview.config] : []}
            noResultsText={
              <>
                <TableEmptyContent
                  title={t(`${dictPrefix}.empty.title`)}
                  subtitle={t(`${dictPrefix}.empty.subtitle`)}
                />
                <Button
                  text={t(`${dictPrefix}.empty.configAction`)}
                  onClick={() => setIsConfigModalOpen(true)}
                  outlined
                  size="sm"
                  className="mx-auto mb-4"
                />
              </>
            }
          />
          {overview.config && (
            <p className="fs-14 text-muted mt-2">
              {getRuleSentence(
                t,
                overview.config.overdueDays,
                overview.config.overdueDebtsCount,
              )}
            </p>
          )}
        </Col>
        <Col xs={12} lg={6}>
          <Row className="align-items-start">
            <Col>
              <SectionTitle text={t(`${dictPrefix}.processesTitle`)} />
              <p>{t(`${dictPrefix}.processesSubtitle`)}</p>
            </Col>
            <Col xs="auto">
              <Button
                text={t(`${dictPrefix}.editProcesses`)}
                onClick={() => setIsProcessesModalOpen(true)}
                size="sm"
                className="mb-1"
                outlined
              />
            </Col>
          </Row>
          <Table
            columns={processColumns}
            data={affectedProcesses}
            noResultsText={
              <>
                <TableEmptyContent
                  title={t(`${dictPrefix}.empty.processesTitle`)}
                  subtitle={t(`${dictPrefix}.empty.processesSubtitle`)}
                />
                <Button
                  text={t(`${dictPrefix}.empty.processesAction`)}
                  onClick={() => setIsProcessesModalOpen(true)}
                  outlined
                  size="sm"
                  className="mx-auto mb-4"
                />
              </>
            }
          />
        </Col>
      </Row>
      <ConfigModal
        isOpen={isConfigModalOpen}
        config={overview.config}
        onClose={() => setIsConfigModalOpen(false)}
        onSaved={handleSaved}
      />
      <ProcessesModal
        isOpen={isProcessesModalOpen}
        processes={overview.processes}
        onClose={() => setIsProcessesModalOpen(false)}
        onSaved={handleSaved}
      />
    </>
  );
}
